import React, { useState } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import type { CollectionStandings } from '@moment-golf/types';

const wrapCell = (params: GridRenderCellParams) => (
    <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35, py: 1 }}>
        {params.value}
    </Box>
);

interface CollectionStandingsDisplayProps {
    standings: CollectionStandings | null | undefined;
    factor?: number;
}

function standingsGridSx(factor: number) {
    return {
        fontSize: 12 * factor,
        border: 'none',
        backgroundColor: 'white',
        '& .MuiDataGrid-main': { backgroundColor: 'white' },
        '& .MuiDataGrid-virtualScroller': {
            overscrollBehavior: 'none',
            backgroundColor: 'white',
        },
        '& .MuiDataGrid-columnHeader': { backgroundColor: 'white' },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            fontSize: 12 * factor,
        },
    };
}

export const CollectionStandingsDisplay: React.FC<CollectionStandingsDisplayProps> = ({
    standings,
    factor = 1,
}) => {
    const hasIndividual = (standings?.individual?.length ?? 0) > 0;
    const hasPairs = (standings?.pairs?.length ?? 0) > 0;
    const hasTeams = (standings?.teams?.length ?? 0) > 0;

    const defaultView: 'individual' | 'pairs' | 'teams' = hasIndividual
        ? 'individual'
        : hasPairs
        ? 'pairs'
        : 'teams';

    const [standingsView, setStandingsView] = useState<'individual' | 'pairs' | 'teams'>(defaultView);

    if (!standings) {
        return (
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: 13 * factor, pt: 2 }}
            >
                No standings have been computed for this series yet.
            </Typography>
        );
    }

    const individualCols: GridColDef[] = [
        { field: 'rank',         headerName: 'Rank',   width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: wrapCell },
        { field: 'playerName',   headerName: 'Player', flex: 1, minWidth: 80,              renderCell: wrapCell },
        { field: 'totalPoints',  headerName: 'Points', width: 66 * factor, type: 'number', align: 'right',  headerAlign: 'right',  renderCell: wrapCell },
        { field: 'eventsPlayed', headerName: 'Events', width: 58 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: wrapCell },
    ];

    const pairsCols: GridColDef[] = [
        { field: 'rank',         headerName: 'Rank',   width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: wrapCell },
        { field: 'pairName',     headerName: 'Pair',   flex: 1, minWidth: 80,              renderCell: wrapCell },
        { field: 'totalPoints',  headerName: 'Points', width: 66 * factor, type: 'number', align: 'right',  headerAlign: 'right',  renderCell: wrapCell },
        { field: 'eventsPlayed', headerName: 'Events', width: 58 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: wrapCell },
    ];

    const teamsCols: GridColDef[] = [
        { field: 'rank',           headerName: 'Rank',   width: 52 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: wrapCell },
        { field: 'teamName',       headerName: 'Team',   flex: 1, minWidth: 80,              renderCell: wrapCell },
        { field: 'totalPoints',    headerName: 'Points', width: 66 * factor, type: 'number', align: 'right',  headerAlign: 'right',  renderCell: wrapCell },
        { field: 'eventsQualified',headerName: 'Events', width: 58 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: wrapCell },
    ];

    const showToggle = [hasIndividual, hasPairs, hasTeams].filter(Boolean).length > 1;

    return (
        <Stack direction="column" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* Header row */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ pb: 1, borderBottom: '1px dashed', borderColor: 'divider', flexShrink: 0 }}
            >
                {/* <Typography
                    variant="subtitle2"
                    color="primary"
                    sx={{ fontWeight: 600, fontSize: 13 * factor }}
                >
                    Series Standings
                </Typography> */}
                {standings.computedAt && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 11 * factor }}
                    >
                        Last updated:{' '}
                        {new Date(standings.computedAt).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                        })}
                    </Typography>
                )}
            </Stack>

            {/* View toggle */}
            {showToggle && (
                <Stack direction="row" alignItems="center" sx={{ pt: 1, pb: 0.5, flexShrink: 0 }}>
                    <ToggleButtonGroup
                        value={standingsView}
                        exclusive
                        onChange={(_, val) => { if (val) setStandingsView(val); }}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                fontSize: 12 * factor,
                                textTransform: 'none',
                                px: 1.5,
                                py: 0.5,
                            },
                        }}
                    >
                        {hasIndividual && <ToggleButton value="individual">Individual</ToggleButton>}
                        {hasPairs && <ToggleButton value="pairs">Pairs</ToggleButton>}
                        {hasTeams && <ToggleButton value="teams">Teams</ToggleButton>}
                    </ToggleButtonGroup>
                </Stack>
            )}

            {/* minWidth: 0 lets the grid shrink to the iframe/parent width */}
            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                {standingsView === 'individual' && hasIndividual && (
                    <DataGridPro
                        rows={standings.individual.map((row, i) => ({ id: i, ...row }))}
                        columns={individualCols}
                        density="compact"
                        getRowHeight={() => 'auto'}
                        disableColumnMenu
                        disableColumnResize
                        disableRowSelectionOnClick
                        hideFooter={standings.individual.length <= 100}
                        sx={{ ...standingsGridSx(factor), width: '100%', height: '100%' }}
                    />
                )}
                {standingsView === 'pairs' && hasPairs && (
                    <DataGridPro
                        rows={standings.pairs.map((row, i) => ({ id: i, ...row }))}
                        columns={pairsCols}
                        density="compact"
                        getRowHeight={() => 'auto'}
                        disableColumnMenu
                        disableColumnResize
                        disableRowSelectionOnClick
                        hideFooter={standings.pairs.length <= 100}
                        sx={{ ...standingsGridSx(factor), width: '100%', height: '100%' }}
                    />
                )}
                {standingsView === 'teams' && hasTeams && (
                    <DataGridPro
                        rows={standings.teams.map((row, i) => ({ id: i, ...row }))}
                        columns={teamsCols}
                        density="compact"
                        getRowHeight={() => 'auto'}
                        disableColumnMenu
                        disableColumnResize
                        disableRowSelectionOnClick
                        hideFooter={standings.teams.length <= 100}
                        sx={{ ...standingsGridSx(factor), width: '100%', height: '100%' }}
                    />
                )}
            </Box>
        </Stack>
    );
};
